import React, { useState, useEffect } from 'react'
import messages from './messages'
import { FormattedMessage } from 'react-intl'
import { Full as Loading } from 'components/Loading'
import { setTheme, applyTheme } from 'themes/utils'
import { Button, Card } from '@arnsbogroup/react-voicearchive'

const PublicLiveSessionRedirectPage = ({ match }) => {
  const hashId = match.params.hashId
  const [loading, setLoading] = useState(false)
  const [redirectUrl, setRedirectUrl] = useState(null)
  const [meetingName, setMeetingName] = useState(null)
  const [countdown, setCountdown] = useState(null)
  const [brand, setBrand] = useState(null)

  useEffect(() => {
    if (!hashId) return

    setLoading(true)
    fetch(`/rest/v2/livesession/${hashId}`)
      .then((response) => response.json())
      .then(({ data, success, errors }) => {
        if (!success && errors) {
          toastr.error(errors.join(', '))
        }
        if (data) {
          setRedirectUrl(data.redirect)
          setMeetingName(data.meetingName)

          if (data.brand) {
            setBrand(data.brand)
            data.brand.theme ? setTheme(data.brand.theme) : applyTheme('base')
          }

          if (data.redirectImmediately) {
            window.location.href = data.redirect
            return
          }

          if (data.startsAt && data.minutesBeforeJoinable) {

            const joinTime = data.startsAt * 1000 - data.minutesBeforeJoinable * 60 * 1000
            const now = Date.now()
            const remainingTime = Math.max(0, Math.floor((joinTime - now) / 1000))
            setCountdown(remainingTime)
          }
        } else {
          applyTheme('base')
        }

        setLoading(false)
      })
      .catch((error) => toastr.error(error))
  }, [hashId])

  useEffect(() => {
    if (countdown === null || countdown <= 0) return

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer)
          window.location.href = redirectUrl
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [countdown, redirectUrl])

  if (loading || !hashId) {
    return <Loading />
  }

  const formatTime = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60
    return [hours, minutes, seconds]
      .map((unit) => String(unit).padStart(2, '0'))
      .join(':')
  }

  return (
    <Card className="flex justify-center items-center min-h-screen bg-gray-100 p-6">
      <div className="bg-white p-8 text-center w-full max-w-lg shadow-lg rounded-lg">
        {redirectUrl ? (
          <div>
              <div className="mb-12">
                <img className="mx-auto w-2/5" src={brand?.logo || brand?.logo_dark} alt="Brand Logo" />
              </div>
              <h1 className="text-2xl font-bold text-gray-800 mb-6">{meetingName}</h1>
        
              {countdown !== null && countdown > 0 ? (
                <>
                  <p className="text-gray-600 mb-2">
                    <FormattedMessage {...messages.redirectCountdown} values={{ time: formatTime(countdown) }} />
                  </p>
                  <p className="text-sm text-gray-500 mb-4">
                    <FormattedMessage {...messages.waitMessage} />
                  </p>
                </>
              ) : (
                <>
                  <p className="text-gray-600 mb-2">
                    <FormattedMessage {...messages.waitingMessage} />
                  </p>
                  <p className="text-sm text-gray-500 mb-4">
                    <FormattedMessage {...messages.readyToJoin} />
                  </p>
                </>
              )}
        
              <Button disabled={countdown > 0} onClick={() => (window.location.href = redirectUrl)}>
                {countdown > 0 ? (
                  <span className="opacity-50 cursor-not-allowed">
                    <FormattedMessage {...messages.joinNow} />
                  </span>
                ) : (
                  <FormattedMessage {...messages.joinNow} />
                )}
              </Button>
        
              {countdown > 0 && (
                <p className="text-xs text-gray-400 mt-2">
                  <FormattedMessage {...messages.buttonDisabledHint} />
                </p>
              )}
          </div>
        ) : (
          <div>
            <FormattedMessage {...messages.liveSessionNotFound} />
          </div>
        )}
      </div>
    </Card>
  )
}

export default PublicLiveSessionRedirectPage
